<template>
  <div ref="paneRoot" class="gc-json-split__pane gc-json-split__pane--preview">
    <div class="gc-json-split__header">
      <div class="gc-json-split__header-text">
        <span class="gc-json-split__title">English (master)</span>
        <span v-if="resolvedPath" class="gc-json-split__subtitle">{{ resolvedPath }} · baseline</span>
        <span v-else class="gc-json-split__subtitle">en.json · baseline</span>
      </div>
      <v-progress-circular
        v-if="loading"
        indeterminate
        size="20"
        width="2"
        color="#f5c347"
      />
    </div>
    <div class="gc-json-split__preview-body">
      <p v-if="!active" class="gc-json-split__empty">Use Compare with English to load</p>
      <p v-else-if="!resolvedPath" class="gc-json-split__empty">Could not resolve English file path</p>
      <p v-else-if="error" class="gc-json-split__error">{{ error }}</p>
      <p v-else-if="!hasContent && !loading" class="gc-json-split__empty">No content loaded</p>
      <vue-json-editor
        v-else
        ref="jsonEditor"
        :value="referenceContent"
        mode="view"
        :show-btns="false"
        :expandedOnStart="true"
        :modes="['view']"
      />
    </div>
  </div>
</template>

<script>
import vueJsonEditor from 'vue-json-editor';
import { getJsonEditorScrollElement } from '@/plugins/sync-json-editor-scroll';
import {
  clearJsonEditorCompareHighlights,
  scheduleJsonEditorCompareHighlights
} from '@/plugins/json-editor-compare-highlight';

export default {
  name: 'EnglishReferencePane',

  components: {
    vueJsonEditor
  },

  props: {
    resolvedPath: {
      type: String,
      default: ''
    },
    storage: {
      type: String,
      required: true
    },
    endpoints: {
      type: Object,
      required: true
    },
    axios: {
      type: Function,
      required: true
    },
    trigger: {
      type: Number,
      default: 0
    },
    active: {
      type: Boolean,
      default: false
    },
    compareDiff: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      referenceContent: null,
      loading: false,
      error: '',
      requestId: 0
    };
  },

  computed: {
    hasContent() {
      return this.referenceContent !== null && this.referenceContent !== undefined;
    }
  },

  watch: {
    trigger() {
      if (this.trigger > 0 && this.active) {
        this.loadReference();
      }
    },
    compareDiff: {
      deep: true,
      handler() {
        this.refreshCompareHighlights();
      }
    },
    referenceContent() {
      this.refreshCompareHighlights();
    }
  },

  beforeDestroy() {
    this.requestId += 1;
    clearJsonEditorCompareHighlights(this.$refs.jsonEditor);
  },

  methods: {
    getScrollElement() {
      return (
        getJsonEditorScrollElement(this.$refs.jsonEditor) ||
        getJsonEditorScrollElement(this.$refs.paneRoot)
      );
    },

    refreshCompareHighlights() {
      if (!this.compareDiff || !this.$refs.jsonEditor) return;
      this.$nextTick(() => {
        scheduleJsonEditorCompareHighlights(this.$refs.jsonEditor, this.compareDiff, 'reference');
      });
    },

    async loadReference() {
      const requestId = ++this.requestId;

      if (!this.resolvedPath) {
        this.referenceContent = {};
        this.error = '';
        this.loading = false;
        this.$emit('reference-ready', this.referenceContent);
        this.$emit('ready');
        return;
      }

      this.loading = true;
      this.error = '';

      try {
        const url = this.endpoints.get.url
          .replace(new RegExp('{storage}', 'g'), this.storage)
          .replace(new RegExp('{path}', 'g'), this.resolvedPath);

        const res = await this.axios.request({
          url,
          method: this.endpoints.get.method || 'get'
        });

        if (requestId !== this.requestId) return;

        let data = res.data;
        if (typeof data === 'string') {
          try {
            data = JSON.parse(data);
          } catch {
            this.error = 'English file is not valid JSON';
            this.referenceContent = {};
            this.loading = false;
            this.$emit('ready');
            return;
          }
        }

        this.referenceContent = data;
        this.error = '';
        this.loading = false;
        this.$emit('reference-ready', this.referenceContent);
        this.$emit('ready');
        this.refreshCompareHighlights();
      } catch (e) {
        if (requestId !== this.requestId) return;
        const status = e.response?.status;
        if (status === 404) {
          this.error = `No English file at ${this.resolvedPath}`;
        } else {
          this.error =
            e.response?.data?.message || e.message || 'Could not load English file';
        }
        this.referenceContent = {};
        this.loading = false;
        this.$emit('ready');
      }
    }
  }
};
</script>
